import axios from 'axios';
import { io, type Socket } from 'socket.io-client';
import { AUTH_URL } from '../config/apiUrls';
import type {
  CallCreatedEvent,
  CallEndedEvent,
  CallErrorEvent,
  CallJoinedEvent,
  CallSignal,
  CallSummary,
  CallType,
  ConnectedEvent,
  IceServer,
  IncomingCallEvent,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  SignalEvent,
} from '../types/calling';
import type { PresenceSelfEvent, PresenceSnapshotEvent, PresenceUpdateEvent } from '../types/connections';

import { attachAuthInterceptors } from './attachAuthInterceptors';

const callsClient = axios.create({ baseURL: AUTH_URL });

attachAuthInterceptors(callsClient);

type ServerToClientEvents = {
  'call:connected': (event: ConnectedEvent) => void;
  'call:created': (event: CallCreatedEvent) => void;
  'call:incoming': (event: IncomingCallEvent) => void;
  'call:joined': (event: CallJoinedEvent) => void;
  'call:participant-joined': (event: ParticipantJoinedEvent) => void;
  'call:participant-left': (event: ParticipantLeftEvent) => void;
  'call:rejected': (event: { callId: string; userId: number }) => void;
  'call:ended': (event: CallEndedEvent) => void;
  'call:signal': (event: SignalEvent) => void;
  'call:list': (event: { calls: CallSummary[] } | CallSummary[]) => void;
  'call:error': (event: CallErrorEvent) => void;
  'presence:snapshot': (event: PresenceSnapshotEvent) => void;
  'presence:update': (event: PresenceUpdateEvent) => void;
  'presence:self': (event: PresenceSelfEvent) => void;
  'presence:admin-snapshot'?: (event: any) => void;
  'presence:admin-update'?: (event: any) => void;
  'call:admin-snapshot'?: (event: any) => void;
  'call:admin-update'?: (event: any) => void;
  [key: string]: any;
};

type ClientToServerEvents = {
  'call:create': (payload: { type: CallType; participantIds: number[] }) => void;
  'call:join': (payload: { callId: string }) => void;
  'call:leave': (payload: { callId: string }) => void;
  'call:reject': (payload: { callId: string }) => void;
  'call:end': (payload: { callId: string }) => void;
  'call:signal': (payload: {
    callId: string;
    targetUserId: number;
    signal: CallSignal;
  }) => void;
  'call:list': () => void;
  'presence:list': () => void;
  'presence:set': (payload: { active: boolean }) => void;
  'presence:admin-list': (payload?: { minutes?: number }) => void;
  'call:admin-list': () => void;
  [key: string]: any;
};

class CallingService {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

  connect(token: string) {
    if (this.socket) {
      this.socket.auth = { token };
      if (!this.socket.connected) this.socket.connect();
      return this.socket;
    }

    this.socket = io(`${AUTH_URL}/signaling`, {
      auth: { token },
      // PM2 multi-worker without sticky sessions breaks the poll→upgrade handshake.
      // WebSocket-only avoids cross-worker XHR polling errors.
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      // Graceful reconnect: backoff quickly, keep trying while offline/online flips.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8_000,
      randomizationFactor: 0.3,
      timeout: 12_000,
    });

    return this.socket;
  }

  getSocket() {
    return this.socket;
  }

  createDirectCall(userId: number) {
    this.socket?.emit('call:create', { type: 'direct', participantIds: [userId] });
  }

  createGroupCall(userIds: number[]) {
    this.socket?.emit('call:create', { type: 'group', participantIds: userIds });
  }

  async fetchIceServers() {
    const response = await callsClient.get<{ iceServers: IceServer[] }>('/calls/ice-servers');
    return response.data.iceServers;
  }

  async fetchActiveCalls() {
    const response = await callsClient.get<CallSummary[]>('/calls/active');
    return response.data;
  }

  joinCall(callId: string) {
    this.socket?.emit('call:join', { callId });
  }

  rejectCall(callId: string) {
    this.socket?.emit('call:reject', { callId });
  }

  leaveCall(callId: string) {
    this.socket?.emit('call:leave', { callId });
  }

  endCall(callId: string) {
    this.socket?.emit('call:end', { callId });
  }

  sendSignal(callId: string, targetUserId: number, signal: CallSignal) {
    this.socket?.emit('call:signal', { callId, targetUserId, signal });
  }

  listCalls() {
    this.socket?.emit('call:list');
  }

  listPresence() {
    this.socket?.emit('presence:list');
  }

  listAdminPresence(minutes?: number) {
    this.socket?.emit('presence:admin-list', minutes ? { minutes } : undefined);
  }

  listAdminCalls() {
    this.socket?.emit('call:admin-list');
  }

  setPresence(active: boolean) {
    this.socket?.emit('presence:set', { active });
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }
}

export const callingService = new CallingService();
